import { Injectable } from '@angular/core';
import { ProductService } from '@epicuro-next/data/product';
import {
  selectMerchandisePricesState,
  setLoadedProductDiscounts,
  State as DiscountsState,
} from '@epicuro-next/state/deprecated-merchandise-prices';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import {
  loadPrices,
  loadPricesFailure,
  loadPricesSuccess,
  updatePricesWithDiscounts,
} from '../actions/config-strip.actions';
import {
  PriceType,
  ProductPriceData,
} from '../models/config-strip-state.models';
import { enrichPricesWithDiscounts } from '../prices/enrich-prices-with-discounts';
import { selectPrices } from '../selectors/data/config-strip.data.selectors';

@Injectable()
export class ConfigStripPricesEffects {
  public loadPrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPrices),
      switchMap(({ products }) => {
        return forkJoin({
          now: this.productService.loadPrices(products, PriceType.Now),
          future: this.productService.loadPrices(products, PriceType.Future),
        }).pipe(
          take(1),
          withLatestFrom(this.store.select(selectMerchandisePricesState)),
          map(([pricesData, discounts]: [ProductPriceData, DiscountsState]) => {
            if (!pricesData.now.length || !pricesData.future.length) {
              throw new Error('Cannot find prices');
            }

            return loadPricesSuccess({
              prices: enrichPricesWithDiscounts(pricesData, discounts),
            });
          }),
          catchError((error: Error) =>
            of(loadPricesFailure({ error: error.message })),
          ),
        );
      }),
    ),
  );

  public updatePricesWithDiscounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setLoadedProductDiscounts),
      withLatestFrom(
        this.store.select(selectMerchandisePricesState),
        this.store.select(selectPrices),
      ),
      filter(
        ([, , prices]) =>
          Object.keys(prices?.now ?? {}).length > 0 &&
          Object.keys(prices?.future ?? {}).length > 0,
      ),
      map(([, merchandisePrices, prices]) => {
        return updatePricesWithDiscounts({
          prices: enrichPricesWithDiscounts(
            Object.keys(prices).reduce(
              (acc: ProductPriceData, key: PriceType) => {
                const pricesData = Object.entries(prices[key]).map(
                  ([productId, innerPrices]) => ({
                    brandId: '',
                    prices: innerPrices,
                    productId,
                  }),
                );

                acc[key] = pricesData;

                return acc;
              },
              {} as ProductPriceData,
            ),
            merchandisePrices,
          ),
        });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private productService: ProductService,
    private store: Store,
  ) {}
}
